import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, doc, getDoc, orderBy, limit, where } from 'firebase/firestore'; 
import { Bar, Pie, Line } from 'react-chartjs-2';
// Chart.js imports are already registered globally in App.js via CDN.
// So, we don't need ChartJS.register here again.

// Helper function to format numbers with ultra-style
const formatNumber = (num) => {
  if (num === null || num === undefined) return 'N/A';
  return num.toLocaleString();
};

// Enhanced helper function to format money with animations
const formatMoney = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(2)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${Math.round(value)}`;
};

// Helper function to get trend color and animation
const getTrendData = (current, previous) => {
  if (!previous || !current) return { color: 'text-gray-400', icon: 'fas fa-minus', animation: 'animate-pulse' };
  
  const change = current - previous;
  const percentage = ((change / previous) * 100).toFixed(1);
  
  if (change > 0) {
    return {
      color: 'text-green-400',
      icon: 'fas fa-arrow-up',
      animation: 'animate-bounce',
      percentage: `+${percentage}%`,
      change: `+${formatMoney(change)}`
    };
  } else if (change < 0) {
    return {
      color: 'text-red-400',
      icon: 'fas fa-arrow-down',
      animation: 'animate-pulse',
      percentage: `${percentage}%`,
      change: formatMoney(change)
    };
  }
  
  return {
    color: 'text-gray-400',
    icon: 'fas fa-minus',
    animation: 'animate-pulse',
    percentage: '0%',
    change: '$0'
  };
};

// Enhanced StatCard Component with Ultra-Hot Interactive Design
const StatCard = ({ title, value, icon, iconColorClass, index, trend, previousValue }) => {
  const trendData = getTrendData(typeof value === 'string' ? parseFloat(value.replace(/[$,KMB]/g, '')) : value, previousValue);
  
  return (
    <div 
      className="group relative overflow-hidden glass-card rounded-xl p-6 hover:bg-white/10 transition-all duration-700 hover:scale-105 hover:-translate-y-2 hover:shadow-2xl border border-white/10 hover:border-green-400/50 animate-fadeIn hover-lift"
      style={{ animationDelay: `${(index || 0) * 0.1}s` }}
    >
      {/* Shimmer overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
      
      {/* Gradient background overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-600/10 via-emerald-600/5 to-green-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      {/* Animated border glow */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-green-400/20 via-transparent to-emerald-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
      
      <div className="relative flex items-center">
        <div className="relative mr-4 flex-shrink-0">
          <div className={`w-14 h-14 rounded-xl ${iconColorClass.replace('text-', 'bg-').replace('400', '500/20')} flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg`}>
            {React.cloneElement(icon, { className: `${icon.props.className} ${iconColorClass} text-xl animate-pulse` })}
          </div>
          {/* Animated ring */}
          <div className={`absolute -inset-1 rounded-xl ${iconColorClass.replace('text-', 'bg-').replace('400', '500/10')} opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300`}></div>
        </div>
        
        <div className="flex-grow min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-bold text-gray-300 uppercase tracking-wide group-hover:text-gray-200 transition-colors duration-300 truncate">{title}</p>
            {trendData.percentage !== '0%' && (
              <div className={`flex items-center space-x-1 ${trendData.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex-shrink-0`}>
                <i className={`${trendData.icon} text-xs ${trendData.animation}`}></i>
                <span className="text-xs font-bold">{trendData.percentage}</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xl font-black text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-green-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-500 transform group-hover:scale-105 truncate">
              {value}
            </p>
            {trendData.change !== '$0' && (
              <span className={`text-xs ${trendData.color} font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
                {trendData.change}
              </span>
            )}
          </div>
          {/* Progress bar effect */}
          <div className="h-0.5 w-0 bg-gradient-to-r from-green-400 to-emerald-400 group-hover:w-full transition-all duration-700 rounded-full mt-2"></div>
        </div>
      </div>
      
      {/* Floating particles */}
      <div className="absolute top-1 right-1 w-1 h-1 rounded-full bg-green-400/60 opacity-0 group-hover:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.2s' }}></div>
      <div className="absolute bottom-1 left-1 w-1 h-1 rounded-full bg-emerald-400/60 opacity-0 group-hover:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.8s' }}></div>
    </div>
  );
};

// Main ServerEconomy Component
export function ServerEconomy({ db, setModalMessage }) { // Receive db and setModalMessage as props
  const [economyData, setEconomyData] = useState({
    moneyInCirculation: [],
    bankBalances: [],
    cashBalances: [],
    vehicleMoney: [],
    factionMoney: [],
    stashMoney: [],
    savingsAccounts: []
  });
  
  const [timeRange, setTimeRange] = useState('week'); // 'day', 'week', 'month'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch economic data from Firestore
  const fetchEconomyData = useCallback(async () => {
    if (!db) {
        setError("Firestore database not available for economy data.");
        setLoading(false);
        return;
    }
    try {
      setLoading(true);
      setError(null);
      
      const now = new Date();
      let startTime = new Date();

      if (timeRange === 'day') {
          startTime.setHours(now.getHours() - 24); // Last 24 hours
      } else if (timeRange === 'week') {
          startTime.setDate(now.getDate() - 7); // Last 7 days
      } else if (timeRange === 'month') {
          startTime.setMonth(now.getMonth() - 1); // Last 30 days (approx)
      }

      // Create queries for all data types
      const queries = {
        moneyInCirculation: query(
          collection(db, 'economy'),
          where('type', '==', 'moneyInCirculation'),
          where('timestamp', '>=', startTime), // Filter by time range
          orderBy('timestamp', 'asc') // Order for chart
        ),
        bankBalances: query(collection(db, 'economy'), where('type', '==', 'bankBalances'), orderBy('timestamp', 'desc'), limit(1)),
        cashBalances: query(collection(db, 'economy'), where('type', '==', 'cashBalances'), orderBy('timestamp', 'desc'), limit(1)),
        vehicleMoney: query(collection(db, 'economy'), where('type', '==', 'vehicleMoney'), orderBy('timestamp', 'desc'), limit(1)),
        factionMoney: query(collection(db, 'economy'), where('type', '==', 'factionMoney'), orderBy('timestamp', 'desc'), limit(1)),
        stashMoney: query(collection(db, 'economy'), where('type', '==', 'stashMoney'), orderBy('timestamp', 'desc'), limit(1)),
        savingsAccounts: query(collection(db, 'economy'), where('type', '==', 'savingsAccounts'), orderBy('timestamp', 'desc'), limit(1))
      };

      // Execute all queries
      const results = {};
      for (const [key, q] of Object.entries(queries)) {
        const querySnapshot = await getDocs(q);
        results[key] = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp ? doc.data().timestamp.toDate() : null // Convert Firestore Timestamp to Date object
        }));
      }

      setEconomyData(results);
      setLoading(false);
    } catch (err) {
      console.error('Error loading economic data:', err);
      setError('Failed to load economic data. ' + err.message);
      setModalMessage(`Failed to load economic data: ${err.message}`);
      setLoading(false);
    }
  }, [db, timeRange, setModalMessage]); // Add db, timeRange, setModalMessage to dependencies

  useEffect(() => {
    fetchEconomyData();
    const interval = setInterval(fetchEconomyData, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, [fetchEconomyData]); // fetchEconomyData is a dependency because it's wrapped in useCallback

  // Chart: Money in circulation over time (Line Chart)
  const circulationChartData = {
    labels: economyData.moneyInCirculation.map(item => 
      item.timestamp ? item.timestamp.toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'}) : ''
    ).reverse(), // Reverse to show oldest first on chart
    datasets: [
      {
        label: 'Total money in circulation',
        data: economyData.moneyInCirculation.map(item => item.circulation).reverse(),
        borderColor: '#4d9eff', // Tailwind blue
        backgroundColor: 'rgba(77, 158, 255, 0.2)', // Slightly transparent blue
        tension: 0.3, // Smooth line
        pointRadius: 3,
        pointBackgroundColor: '#316dae',
        fill: true,
      }
    ]
  };

  // Chart: Money distribution (Pie Chart)
  const latestCirculation = economyData.moneyInCirculation[economyData.moneyInCirculation.length - 1] || {}; // Get latest entry
  const moneyDistributionData = {
    labels: ['Banks', 'Cash', 'Vehicles', 'Factions', 'Stashes', 'Savings'],
    datasets: [
      {
        data: [
          latestCirculation.bank || 0,
          latestCirculation.cash || 0,
          latestCirculation.vehicles || 0,
          latestCirculation.factions || 0,
          latestCirculation.stashes || 0,
          latestCirculation.savings || 0
        ],
        backgroundColor: [
          '#6366f1', // Indigo
          '#0ea5e9', // Sky
          '#f59e0b', // Amber
          '#10b981', // Emerald
          '#ef4444', // Red
          '#a855f7'  // Purple
        ],
        hoverOffset: 8, // Pop out effect on hover
      }
    ]
  };

  // Chart: Category comparison (Bar Chart)
  const categoryComparisonData = {
    labels: ['Banks', 'Cash', 'Vehicles', 'Factions', 'Stashes', 'Savings'],
    datasets: [{
      label: 'Monetary Amounts',
      data: [
        latestCirculation.bank || 0,
        latestCirculation.cash || 0,
        latestCirculation.vehicles || 0,
        latestCirculation.factions || 0,
        latestCirculation.stashes || 0,
        latestCirculation.savings || 0
      ],
      backgroundColor: '#4d9eff', // Consistent blue
      hoverBackgroundColor: '#316dae',
    }]
  };


  // Top 10 players by bank account
  const topBankPlayers = economyData.bankBalances[0]?.topPlayers || [];

  // Top 10 factions by money
  const topFactions = economyData.factionMoney[0]?.topFactions || [];


  if (loading) {
    return (
      <div className="glass-card rounded-2xl h-full flex items-center justify-center p-12 animate-fadeIn overflow-hidden">
        <div className="text-center relative">
          {/* Enhanced loading animations with multiple layers */}
          <div className="relative mb-8">
            {/* Outer rotating ring */}
            <div className="animate-spin rounded-full h-24 w-24 border-4 border-green-500/20 border-t-green-500 mx-auto animate-glow"></div>
            {/* Inner pulsing circle */}
            <div className="absolute inset-4 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 animate-pulse"></div>
            {/* Center icon with float animation */}
            <div className="absolute inset-0 flex items-center justify-center">
              <i className="fas fa-dollar-sign text-2xl text-green-400 animate-float"></i>
            </div>
            {/* Floating particles */}
            <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-green-400/60 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="absolute bottom-2 left-2 w-2 h-2 rounded-full bg-emerald-400/60 animate-bounce" style={{ animationDelay: '0.8s' }}></div>
            <div className="absolute top-2 left-2 w-1 h-1 rounded-full bg-lime-400/60 animate-bounce" style={{ animationDelay: '1.2s' }}></div>
          </div>
          <div className="space-y-4">
            <h3 className="text-3xl font-bold gradient-text animate-slideInLeft">Loading Economy Data</h3>
            <p className="text-gray-300 text-lg animate-fadeIn" style={{ animationDelay: '0.3s' }}>
              Analyzing server financial statistics...
            </p>
            <div className="flex items-center justify-center space-x-3 animate-slideInLeft" style={{ animationDelay: '0.5s' }}>
              <div className="w-2 h-2 rounded-full bg-green-400 animate-bounce"></div>
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 rounded-full bg-lime-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            {/* Progress indicators */}
            <div className="mt-6 space-y-2 animate-fadeIn" style={{ animationDelay: '0.7s' }}>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
                <i className="fas fa-chart-line text-green-400"></i>
                <span>Fetching circulation data...</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
                <i className="fas fa-university text-emerald-400"></i>
                <span>Loading bank statistics...</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
                <i className="fas fa-users text-lime-400"></i>
                <span>Analyzing player wealth...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card rounded-2xl h-full flex items-center justify-center p-12 animate-fadeIn">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-2xl flex items-center justify-center mx-auto shadow-lg animate-bounce">
              <i className="fas fa-exclamation-triangle text-4xl text-red-400 animate-pulse"></i>
            </div>
            <div className="absolute -inset-2 bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl animate-ping"></div>
            {/* Error particles */}
            <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-400/60 animate-bounce" style={{ animationDelay: '0.3s' }}></div>
            <div className="absolute bottom-1 left-1 w-2 h-2 rounded-full bg-orange-400/60 animate-bounce" style={{ animationDelay: '0.7s' }}></div>
          </div>
          <div className="space-y-4">
            <h3 className="text-3xl font-bold gradient-text animate-slideInLeft">Economy Error</h3>
            <p className="text-gray-300 text-lg mb-6 animate-fadeIn" style={{ animationDelay: '0.2s' }}>{error}</p>
            <div className="space-y-2 animate-slideInLeft" style={{ animationDelay: '0.4s' }}>
              <p className="text-sm text-gray-400">Please check your connection and try again</p>
              <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                <i className="fas fa-database"></i>
                <span>Firestore connection and security rules</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl h-full flex flex-col p-6 animate-fadeIn overflow-hidden">
      {/* Ultra-Enhanced Header Section with Floating Elements */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 animate-slideInLeft flex-shrink-0">
        <div className="mb-4 lg:mb-0 relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl blur-xl animate-pulse"></div>
          <div className="relative">
            <h2 className="text-3xl font-black gradient-text flex items-center mb-3 animate-slideInLeft">
              <div className="relative mr-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center shadow-lg animate-float">
                  <i className="fas fa-dollar-sign text-2xl text-green-400 animate-pulse"></i>
                </div>
                <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 animate-ping opacity-75"></div>
              </div>
              <div>
                Server Economy
                <div className="h-1 w-0 bg-gradient-to-r from-green-400 to-emerald-400 animate-[width-expand_2s_ease-out_forwards] rounded-full"></div>
              </div>
            </h2>
            <div className="flex items-center space-x-4 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center space-x-2 text-gray-300">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-sm font-medium">Financial analysis active</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-500 text-xs">
                <i className="fas fa-sync-alt animate-spin" style={{ animationDuration: '3s' }}></i>
                <span>Real-time updates</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Ultra-Interactive Time Range Selector */}
        <div className="relative group animate-bounceIn" style={{ animationDelay: '0.5s' }}>
          <div className="glass-card rounded-xl p-2 border border-white/20 group-hover:border-green-400/50 transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-green-500/25">
            <div className="flex gap-2">
              {[
                { key: 'day', label: '24 hours', icon: 'fas fa-clock' },
                { key: 'week', label: '7 days', icon: 'fas fa-calendar-week' },
                { key: 'month', label: '30 days', icon: 'fas fa-calendar-alt' }
              ].map((range, index) => (
                <button 
                  key={range.key}
                  className={`relative overflow-hidden px-4 py-2 rounded-lg text-xs font-bold transition-all duration-500 transform hover:scale-105 cursor-pointer z-10 ${
                    timeRange === range.key 
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg animate-glow' 
                      : 'text-gray-400 hover:text-white hover:bg-white/10 hover:shadow-lg'
                  }`}
                  onClick={() => setTimeRange(range.key)}
                  style={{ 
                    animationDelay: `${index * 0.1}s`,
                    pointerEvents: 'auto'
                  }}
                >
                  {timeRange === range.key && (
                    <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-emerald-400/20 animate-pulse pointer-events-none"></div>
                  )}
                  <div className="relative flex items-center space-x-1.5 pointer-events-none">
                    <i className={`${range.icon} text-xs`}></i>
                    <span className="text-xs">{range.label}</span>
                  </div>
                  {timeRange === range.key && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer pointer-events-none"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
          {/* Glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-xl opacity-0 group-hover:opacity-100 blur-xl transition-all duration-500 pointer-events-none"></div>
        </div>
      </div>

      {/* Ultra-Enhanced Economic Snapshot Section */}
      <div className="relative overflow-hidden glass-card rounded-xl p-6 mb-6 border border-white/20 hover:border-green-400/50 transition-all duration-500 animate-slideInLeft flex-shrink-0" style={{ animationDelay: '0.2s' }}>
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 via-emerald-600/3 to-green-600/5 opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
        
        <div className="relative">
          <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
            <div className="relative mr-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                <i className="fas fa-money-bill-wave text-green-400 text-lg"></i>
              </div>
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-green-500/10 to-emerald-500/10 animate-ping opacity-50"></div>
            </div>
            <div>
              Current Economic Snapshot
              <div className="h-0.5 w-0 bg-gradient-to-r from-green-400 to-emerald-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
            </div>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard 
              title="Total Circulation" 
              value={formatMoney(latestCirculation.circulation)} 
              icon={<i className="fas fa-hand-holding-usd"></i>} 
              iconColorClass="text-green-400"
              index={0}
            />
            <StatCard 
              title="In Banks" 
              value={formatMoney(latestCirculation.bank)} 
              icon={<i className="fas fa-university"></i>} 
              iconColorClass="text-blue-400"
              index={1}
            />
            <StatCard 
              title="In Cash" 
              value={formatMoney(latestCirculation.cash)} 
              icon={<i className="fas fa-wallet"></i>} 
              iconColorClass="text-yellow-400"
              index={2}
            />
            <StatCard 
              title="In Vehicles" 
              value={formatMoney(latestCirculation.vehicles)} 
              icon={<i className="fas fa-car"></i>} 
              iconColorClass="text-red-400"
              index={3}
            />
          </div>
        </div>
      </div>

      {/* Ultra-Enhanced Main Charts Section */} 
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 flex-shrink-0">
        <div className="relative group overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-blue-400/50 transition-all duration-500 flex flex-col animate-fadeIn" style={{ animationDelay: '0.4s' }}>
          {/* Background animation */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-cyan-600/3 to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
          
          <div className="relative flex-shrink-0">
            <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
              <div className="relative mr-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                  <i className="fas fa-chart-line text-blue-400 text-lg"></i>
                </div>
                <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-blue-500/10 to-cyan-500/10 animate-ping opacity-50"></div>
              </div>
              <div>
                Money Circulation Trends
                <div className="h-0.5 w-0 bg-gradient-to-r from-blue-400 to-cyan-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
              </div>
            </h3>
          </div>
          
          <div className="relative flex-1 min-h-[300px]">
            {/* Chart container with enhanced styling */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative h-full">
              <Line 
                data={circulationChartData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { 
                      display: true,
                      labels: { 
                        color: '#ffffff',
                        font: { weight: 'bold', size: 12 },
                        usePointStyle: true,
                        pointStyle: 'circle'
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => `💰 ${formatMoney(context.parsed.y)}`
                      },
                      bodyColor: '#ffffff',
                      titleColor: '#4d9eff',
                      backgroundColor: 'rgba(0,0,0,0.9)',
                      borderColor: '#4d9eff',
                      borderWidth: 2,
                      cornerRadius: 10
                    }
                  },
                  scales: {
                    x: {
                      ticks: { 
                        color: '#9CA3AF',
                        font: { weight: 'bold' }
                      },
                      grid: { 
                        color: 'rgba(77, 158, 255, 0.1)',
                        drawBorder: false
                      }
                    },
                    y: {
                      ticks: { 
                        color: '#9CA3AF', 
                        callback: (value) => formatMoney(value),
                        font: { weight: 'bold' }
                      },
                      grid: { 
                        color: 'rgba(77, 158, 255, 0.1)',
                        drawBorder: false
                      }
                    }
                  },
                  elements: {
                    line: {
                      borderWidth: 3,
                      tension: 0.4
                    },
                    point: {
                      radius: 4,
                      hoverRadius: 8,
                      borderWidth: 2
                    }
                  }
                }} 
              />
            </div>
          </div>
        </div>
        
        <div className="relative group overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-purple-400/50 transition-all duration-500 flex flex-col animate-fadeIn" style={{ animationDelay: '0.5s' }}>
          {/* Background animation */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-pink-600/3 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
          
          <div className="relative flex-shrink-0">
            <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
              <div className="relative mr-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                  <i className="fas fa-chart-pie text-purple-400 text-lg"></i>
                </div>
                <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-purple-500/10 to-pink-500/10 animate-ping opacity-50"></div>
              </div>
              <div>
                Money Distribution
                <div className="h-0.5 w-0 bg-gradient-to-r from-purple-400 to-pink-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
              </div>
            </h3>
          </div>
          
          <div className="relative flex-1 min-h-[300px] flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative h-full w-full">
              <Pie 
                data={moneyDistributionData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { 
                      position: 'bottom',
                      labels: { 
                        color: '#ffffff',
                        font: { weight: 'bold', size: 11 },
                        padding: 15,
                        usePointStyle: true
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => 
                          `💰 ${context.label}: ${formatMoney(context.parsed)}`
                      },
                      bodyColor: '#ffffff',
                      titleColor: '#a855f7',
                      backgroundColor: 'rgba(0,0,0,0.9)',
                      borderColor: '#a855f7',
                      borderWidth: 2,
                      cornerRadius: 10
                    }
                  },
                  elements: {
                    arc: {
                      borderWidth: 3,
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                      hoverBorderWidth: 4
                    }
                  }
                }} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Ultra-Enhanced TOP Lists Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 flex-shrink-0">
        {/* Ultra-Interactive Top Wealthy Players */}
        <div className="relative group overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-yellow-400/50 transition-all duration-500 flex flex-col animate-fadeIn" style={{ animationDelay: '0.6s' }}>
          {/* Background animation */}
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/5 via-amber-600/3 to-yellow-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
          
          <div className="relative flex-shrink-0">
            <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
              <div className="relative mr-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-500/20 to-amber-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                  <i className="fas fa-trophy text-yellow-400 text-lg"></i>
                </div>
                <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-yellow-500/10 to-amber-500/10 animate-ping opacity-50"></div>
              </div>
              <div>
                Top Wealthy Players
                <div className="h-0.5 w-0 bg-gradient-to-r from-yellow-400 to-amber-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
              </div>
            </h3>
          </div>
          
          <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 min-h-0">
            {topBankPlayers.length === 0 ? (
              <div className="glass-card rounded-xl p-8 text-center animate-fadeIn">
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-500/10 to-amber-500/10 animate-ping" style={{ animationDuration: '3s' }}></div>
                  <div className="absolute inset-4 rounded-full bg-gradient-to-r from-yellow-500/5 to-amber-500/5 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                  
                  <div className="relative w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-yellow-600/20 to-amber-600/20 flex items-center justify-center animate-float">
                    <i className="fas fa-users text-3xl text-yellow-400 animate-pulse"></i>
                  </div>
                </div>
                <h4 className="text-lg font-bold gradient-text mb-3">No Player Data</h4>
                <p className="text-gray-400">Player wealth data not available</p>
              </div>
            ) : (
              topBankPlayers.slice(0, 10).map((player, index) => (
                <div 
                  key={player.id || index} 
                  className="group/player relative overflow-hidden glass-card rounded-xl p-4 hover:bg-white/10 transition-all duration-700 hover:scale-[1.02] hover:-translate-y-1 border border-white/10 hover:border-yellow-400/50 animate-fadeIn hover:shadow-xl hover:shadow-yellow-500/25"
                  style={{ animationDelay: `${0.7 + index * 0.05}s` }}
                >
                  {/* Enhanced hover effects */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover/player:translate-x-full transition-transform duration-1000"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/10 via-amber-600/5 to-yellow-600/10 opacity-0 group-hover/player:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="relative mr-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shadow-lg group-hover/player:scale-110 group-hover/player:rotate-6 transition-all duration-500 ${
                            index === 0 ? 'bg-gradient-to-br from-yellow-500/30 to-amber-500/30 text-yellow-300 animate-glow' :
                            index === 1 ? 'bg-gradient-to-br from-gray-400/30 to-slate-400/30 text-gray-300' :
                            index === 2 ? 'bg-gradient-to-br from-orange-500/30 to-red-500/30 text-orange-300' :
                            'bg-gradient-to-br from-blue-500/30 to-cyan-500/30 text-blue-300'
                          }`}>
                            {index === 0 ? <i className="fas fa-crown text-sm animate-pulse"></i> : index + 1}
                          </div>
                          <div className={`absolute -inset-1 rounded-xl opacity-0 group-hover/player:opacity-100 animate-ping transition-opacity duration-300 ${
                            index === 0 ? 'bg-yellow-500/20' :
                            index === 1 ? 'bg-gray-400/20' :
                            index === 2 ? 'bg-orange-500/20' :
                            'bg-blue-500/20'
                          }`}></div>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-bold group-hover/player:text-transparent group-hover/player:bg-gradient-to-r group-hover/player:from-yellow-400 group-hover/player:to-amber-400 group-hover/player:bg-clip-text transition-all duration-500">
                              {player.name || 'Unknown Player'}
                            </span>
                            {index < 3 && (
                              <i className={`fas fa-medal text-xs ${
                                index === 0 ? 'text-yellow-400 animate-glow' :
                                index === 1 ? 'text-gray-400' : 'text-orange-400'
                              }`}></i>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 font-mono bg-gray-800/50 px-2 py-1 rounded group-hover/player:bg-yellow-500/20 transition-colors">
                            {player.identifier || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold group-hover/player:text-green-300 transition-colors text-lg">
                          {formatMoney(player.balance)}
                        </p>
                        {index === 0 && (
                          <span className="text-xs text-yellow-400 font-semibold animate-pulse">👑 Richest</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Progress bar effect */}
                    <div className="h-0.5 w-0 bg-gradient-to-r from-yellow-400 to-amber-400 group-hover/player:w-full transition-all duration-700 rounded-full mt-3"></div>
                  </div>
                  
                  {/* Floating particles */}
                  <div className="absolute top-1 right-1 w-1 h-1 rounded-full bg-yellow-400/60 opacity-0 group-hover/player:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.2s' }}></div>
                  <div className="absolute bottom-1 left-1 w-1 h-1 rounded-full bg-amber-400/60 opacity-0 group-hover/player:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.8s' }}></div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Ultra-Interactive Top Wealthy Factions */}
        <div className="relative group overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-purple-400/50 transition-all duration-500 flex flex-col animate-fadeIn" style={{ animationDelay: '0.7s' }}>
          {/* Background animation */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-pink-600/3 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
          
          <div className="relative flex-shrink-0">
            <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
              <div className="relative mr-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                  <i className="fas fa-shield-alt text-purple-400 text-lg"></i>
                </div>
                <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-purple-500/10 to-pink-500/10 animate-ping opacity-50"></div>
              </div>
              <div>
                Top Wealthy Factions
                <div className="h-0.5 w-0 bg-gradient-to-r from-purple-400 to-pink-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
              </div>
            </h3>
          </div>
          
          <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3 min-h-0">
            {topFactions.length === 0 ? (
              <div className="glass-card rounded-xl p-8 text-center animate-fadeIn">
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 animate-ping" style={{ animationDuration: '3s' }}></div>
                  <div className="absolute inset-4 rounded-full bg-gradient-to-r from-purple-500/5 to-pink-500/5 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                  
                  <div className="relative w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-purple-600/20 to-pink-600/20 flex items-center justify-center animate-float">
                    <i className="fas fa-shield-alt text-3xl text-purple-400 animate-pulse"></i>
                  </div>
                </div>
                <h4 className="text-lg font-bold gradient-text mb-3">No Faction Data</h4>
                <p className="text-gray-400">Faction wealth data not available</p>
              </div>
            ) : (
              topFactions.slice(0, 10).map((faction, index) => (
                <div 
                  key={faction.name || index} 
                  className="group/faction relative overflow-hidden glass-card rounded-xl p-4 hover:bg-white/10 transition-all duration-700 hover:scale-[1.02] hover:-translate-y-1 border border-white/10 hover:border-purple-400/50 animate-fadeIn hover:shadow-xl hover:shadow-purple-500/25"
                  style={{ animationDelay: `${0.8 + index * 0.05}s` }}
                >
                  {/* Enhanced hover effects */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover/faction:translate-x-full transition-transform duration-1000"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 via-pink-600/5 to-purple-600/10 opacity-0 group-hover/faction:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="relative mr-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shadow-lg group-hover/faction:scale-110 group-hover/faction:rotate-6 transition-all duration-500 ${
                            index === 0 ? 'bg-gradient-to-br from-yellow-500/30 to-amber-500/30 text-yellow-300 animate-glow' :
                            index === 1 ? 'bg-gradient-to-br from-gray-400/30 to-slate-400/30 text-gray-300' :
                            index === 2 ? 'bg-gradient-to-br from-orange-500/30 to-red-500/30 text-orange-300' :
                            'bg-gradient-to-br from-purple-500/30 to-pink-500/30 text-purple-300'
                          }`}>
                            {index === 0 ? <i className="fas fa-crown text-sm animate-pulse"></i> : index + 1}
                          </div>
                          <div className={`absolute -inset-1 rounded-xl opacity-0 group-hover/faction:opacity-100 animate-ping transition-opacity duration-300 ${
                            index === 0 ? 'bg-yellow-500/20' :
                            index === 1 ? 'bg-gray-400/20' :
                            index === 2 ? 'bg-orange-500/20' :
                            'bg-purple-500/20'
                          }`}></div>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-bold group-hover/faction:text-transparent group-hover/faction:bg-gradient-to-r group-hover/faction:from-purple-400 group-hover/faction:to-pink-400 group-hover/faction:bg-clip-text transition-all duration-500">
                              {faction.name || 'Unknown Faction'}
                            </span>
                            {index < 3 && (
                              <i className={`fas fa-medal text-xs ${
                                index === 0 ? 'text-yellow-400 animate-glow' :
                                index === 1 ? 'text-gray-400' : 'text-orange-400'
                              }`}></i>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold group-hover/faction:text-green-300 transition-colors text-lg">
                          {formatMoney(faction.money)}
                        </p>
                        {index === 0 && (
                          <span className="text-xs text-yellow-400 font-semibold animate-pulse">👑 Richest</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Progress bar effect */}
                    <div className="h-0.5 w-0 bg-gradient-to-r from-purple-400 to-pink-400 group-hover/faction:w-full transition-all duration-700 rounded-full mt-3"></div>
                  </div>
                  
                  {/* Floating particles */}
                  <div className="absolute top-1 right-1 w-1 h-1 rounded-full bg-purple-400/60 opacity-0 group-hover/faction:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.2s' }}></div>
                  <div className="absolute bottom-1 left-1 w-1 h-1 rounded-full bg-pink-400/60 opacity-0 group-hover/faction:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.8s' }}></div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Ultra-Enhanced Category Comparison Chart */}
      <div className="relative group overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-indigo-400/50 transition-all duration-500 flex flex-col animate-fadeIn" style={{ animationDelay: '0.8s' }}>
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 via-violet-600/3 to-indigo-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
        
        <div className="relative flex-shrink-0">
          <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
            <div className="relative mr-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500/20 to-violet-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                <i className="fas fa-chart-bar text-indigo-400 text-lg"></i>
              </div>
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-indigo-500/10 to-violet-500/10 animate-ping opacity-50"></div>
            </div>
            <div>
              Category Comparison
              <div className="h-0.5 w-0 bg-gradient-to-r from-indigo-400 to-violet-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
            </div>
          </h3>
        </div>
        
        <div className="relative flex-1 min-h-[300px]">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-violet-500/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          <div className="relative h-full">
            <Bar 
              data={categoryComparisonData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { 
                    display: true,
                    labels: { 
                      color: '#ffffff',
                      font: { weight: 'bold', size: 12 },
                      usePointStyle: true
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => `💰 ${formatMoney(context.parsed.y)}`
                    },
                    bodyColor: '#ffffff',
                    titleColor: '#6366f1',
                    backgroundColor: 'rgba(0,0,0,0.9)',
                    borderColor: '#6366f1',
                    borderWidth: 2,
                    cornerRadius: 10
                  }
                },
                scales: {
                  x: {
                    ticks: { 
                      color: '#9CA3AF',
                      font: { weight: 'bold' }
                    },
                    grid: { 
                      color: 'rgba(99, 102, 241, 0.1)',
                      drawBorder: false
                    }
                  },
                  y: {
                    ticks: { 
                      color: '#9CA3AF', 
                      callback: (value) => formatMoney(value),
                      font: { weight: 'bold' }
                    },
                    grid: { 
                      color: 'rgba(99, 102, 241, 0.1)',
                      drawBorder: false
                    }
                  }
                },
                elements: {
                  bar: {
                    borderRadius: {
                      topLeft: 8,
                      topRight: 8
                    },
                    borderWidth: 2,
                    borderColor: 'rgba(99, 102, 241, 0.3)'
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServerEconomy;